import { keepPreviousData, useQuery } from '@tanstack/react-query';
import React, { useMemo } from 'react';

import { type BankAccountWithDetails, bankAccountListBankAccountsOptions } from '@column/api';
import { Icon } from '@column/column-ui-kit';
import {
  Badge,
  type DataTableColumnDef,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  Timestamp,
} from '@column/ui';

import { type ReportingTableRow } from '~/components/ReportingTable';
import { StatusBadge } from '~/components/Table';
import { TransferProgress } from '~/lib/transfers';
import { getTransferProgressIcon } from '~/lib/transfers/progress';
import { getStatusIconClassName } from '~/lib/transfers/status';
import { getTransferBadgeClassName } from '~/lib/transfers/utils';
import { TableTimeFormat } from '~/repositories/UserRepository';
import { ReportingSettlementReport } from '~/typings/API';
import { formatDateTime, formatString, getFormattedDate } from '~/util';
import { formatReportDate, getReportTypeLabel, getStatementPeriod } from '~/util/reporting';

import { StatementPeriod } from './StatementPeriod';

interface StatementTableColumnsOptions {
  timeFormat: TableTimeFormat;
  onTimeFormatChange: (value: TableTimeFormat) => void;
  accountsById: Map<string, BankAccountWithDetails>;
  hideAccount?: boolean;
}

export const MONTHLY_STATEMENT_TYPE = 'bank_account_monthly_statement';
export const DAILY_STATEMENT_TYPE = 'bank_account_daily_statement';

const BANK_ACCOUNT_ID_PREFIX = 'bacc_';
const BANK_ACCOUNT_LOOKUP_LIMIT = 100;

export const useStatementAccounts = (entries: ReportingSettlementReport[]) => {
  const bankAccountIds = useMemo(
    () =>
      Array.from(
        new Set(
          entries
            .map((entry) => entry.statementSubject)
            .filter((subject): subject is string => !!subject?.startsWith(BANK_ACCOUNT_ID_PREFIX))
        )
      )
        .sort()
        .slice(0, BANK_ACCOUNT_LOOKUP_LIMIT),
    [entries]
  );
  const { data, isLoading, isPlaceholderData } = useQuery({
    ...bankAccountListBankAccountsOptions({
      query: { bank_account_ids: bankAccountIds.join(','), limit: BANK_ACCOUNT_LOOKUP_LIMIT },
    }),
    enabled: bankAccountIds.length > 0,
    placeholderData: keepPreviousData,
  });
  const accountsById = useMemo(
    () => new Map((data?.bank_accounts ?? []).map((bankAccount) => [bankAccount.id, bankAccount])),
    [data]
  );
  return { accountsById, isLoading: isLoading || (bankAccountIds.length > 0 && isPlaceholderData) };
};

const StatementName: React.FC<{ entry: ReportingSettlementReport; account?: BankAccountWithDetails }> = ({
  entry,
  account,
}) => {
  const displayName = account?.display_name?.trim();
  const description = account?.description?.trim();
  const primary = entry.statementSubject
    ? displayName || description || entry.statementSubjectDescription
    : 'All Accounts';
  const secondary = displayName && description !== displayName ? description : undefined;

  if (!primary) return <span className="text-sm text-muted-foreground">-</span>;

  return (
    <div className="flex w-full min-w-0 max-w-full items-center gap-2 py-1">
      <span className="min-w-0 max-w-full shrink-0 truncate text-sm font-medium leading-5 text-foreground">
        {primary}
      </span>
      {secondary && <span className="min-w-0 truncate text-xs font-normal text-muted-foreground">{secondary}</span>}
    </div>
  );
};

const STATUS_PROGRESS: Record<string, TransferProgress> = {
  scheduled: TransferProgress.PROGRESS_0,
  initiated: TransferProgress.PROGRESS_50,
  completed: TransferProgress.PROGRESS_100,
  failed: TransferProgress.PROGRESS_FAILED,
};

const getStatusTone = (progress: TransferProgress) => {
  if (progress === TransferProgress.PROGRESS_100) return 'success';
  if (progress === TransferProgress.PROGRESS_FAILED) return 'danger';
  return 'secondary';
};

export const statementTableColumns = ({
  timeFormat,
  onTimeFormatChange,
  accountsById,
  hideAccount = false,
}: StatementTableColumnsOptions): DataTableColumnDef<ReportingTableRow>[] => {
  const accountColumn: DataTableColumnDef<ReportingTableRow> = {
    id: 'account',
    header: 'Account',
    accessor: 'entry',
    width: 'minmax(300px, auto)',
    className: 'min-w-0',
    cell: ({ row }) => <StatementName entry={row.entry} account={accountsById.get(row.entry.statementSubject ?? '')} />,
  };

  return [
    ...(hideAccount ? [] : [accountColumn]),
    {
      id: 'period',
      header: 'Period',
      accessor: 'entry',
      width: hideAccount ? 'auto' : 'max-content',
      cell: ({ row }) => {
        const period = getStatementPeriod(row.entry.type, row.entry.toDate);
        return (
          <div className="flex items-center gap-1 py-1 pr-4 font-medium">
            {period && (hideAccount || row.entry.type === DAILY_STATEMENT_TYPE) ? (
              <span className="text-sm text-foreground">{period.label}</span>
            ) : period ? (
              <StatementPeriod type={row.entry.type} toDate={row.entry.toDate} />
            ) : (
              <>
                {row.entry.fromDate}
                <Icon.ArrowRight className="text-muted-foreground [--icon-size-x:16px] [--icon-size-y:16px]" />
                {row.entry.toDate}
              </>
            )}
          </div>
        );
      },
    },
    reportStatusColumn,
    reportCreatedColumn(timeFormat, onTimeFormatChange),
  ];
};

export const reportStatusColumn: DataTableColumnDef<ReportingTableRow> = {
  id: 'status',
  header: 'Status',
  accessor: 'entry',
  width: 'minmax(min-content, 150px)',
  cell: ({ row }) => {
    const progress = STATUS_PROGRESS[row.entry.status ?? ''] ?? TransferProgress.UNKNOWN;
    const tone = getStatusTone(progress);
    return (
      <Badge variant="ghost" className={getTransferBadgeClassName(tone)}>
        <span className={getStatusIconClassName(tone)}>{getTransferProgressIcon(progress)}</span>
        {formatString(row.entry.status)}
      </Badge>
    );
  },
};

export const reportCreatedColumn = (
  timeFormat: TableTimeFormat,
  onTimeFormatChange: (value: TableTimeFormat) => void
): DataTableColumnDef<ReportingTableRow> => ({
  id: 'created',
  accessor: 'entry',
  header: (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger className="inline-flex items-center gap-1 outline-none [&_svg]:[--icon-color:currentColor]">
        Created <Icon.ChevronDown />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuRadioGroup
          value={timeFormat}
          onValueChange={(value) => onTimeFormatChange(value as TableTimeFormat)}
        >
          <DropdownMenuRadioItem value={TableTimeFormat.Relative}>Relative Time</DropdownMenuRadioItem>
          <DropdownMenuRadioItem value={TableTimeFormat.Absolute}>Date / Time</DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  ),
  width: 'min-content',
  cell: ({ row }) => {
    const value = row.entry.createdAt;
    if (!value) return null;
    return (
      <Timestamp
        date={value}
        className="text-xs text-muted-foreground"
        formatHover={(hoverDate) => formatDateTime(hoverDate, { timeZoneFormat: 'local' })}
      >
        {timeFormat === TableTimeFormat.Relative
          ? getFormattedDate(value, 'time-passed')
          : formatDateTime(value, { timeZoneFormat: 'local' })}
      </Timestamp>
    );
  },
});

export const reportTableColumns = ({
  timeFormat,
  onTimeFormatChange,
}: Pick<
  StatementTableColumnsOptions,
  'timeFormat' | 'onTimeFormatChange'
>): DataTableColumnDef<ReportingTableRow>[] => [
  {
    id: 'period',
    header: 'Period',
    accessor: 'entry',
    width: 'auto',
    cell: ({ row }) => (
      <div className="flex items-center gap-1 py-1 pr-4 text-sm font-medium text-foreground">
        {!row.entry.fromDate || !row.entry.toDate ? (
          '-'
        ) : row.entry.fromDate === row.entry.toDate ? (
          formatReportDate(row.entry.toDate)
        ) : (
          <>
            {formatReportDate(row.entry.fromDate)}
            <Icon.ArrowRight className="text-muted-foreground [--icon-size-x:16px] [--icon-size-y:16px]" />
            {formatReportDate(row.entry.toDate)}
          </>
        )}
      </div>
    ),
  },
  {
    id: 'report',
    header: 'Report',
    accessor: 'entry',
    width: 'max-content',
    cell: ({ row }) => (
      <div className="pr-6">
        <StatusBadge>{getReportTypeLabel(row.entry.type)}</StatusBadge>
      </div>
    ),
  },
  reportStatusColumn,
  reportCreatedColumn(timeFormat, onTimeFormatChange),
];
